import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useOrderedCol, useProgress, useSiteSettings, useDoc } from '../lib/hooks';
import { useAuthCtx } from '../lib/auth-context';
import { useCurrentCourse } from '../lib/course';
import { gamify, type ChapterDoc, type CourseDoc, type ResourceDoc, type SectionDoc } from '../lib/data-model';
import { Loading, ErrorBlock, EmptyState } from '../components/ui';
import { AdSlot } from '../components/AdSlot';
import { paletteFor, pctOf } from './public-helpers/misc';

type ViewMode = 'tall' | 'wide';

export default function Home() {
  const params = useParams<{ courseId?: string }>();
  const [courseId, setCourseId] = useCurrentCourse();
  const auth = useAuthCtx();
  const progress = useProgress(auth.isSignedIn);
  const [view, setView] = useState<ViewMode>('tall');

  const routeCourse = params.courseId;

  useEffect(() => {
    if (routeCourse && routeCourse !== courseId) setCourseId(routeCourse);
  }, [routeCourse, courseId, setCourseId]);

  const key = routeCourse ?? courseId;

  // NOTE: composite indexes (courseId+order) are not deployed in this project,
  // so we read whole collections ordered by `order` and filter client-side.
  const courses = useOrderedCol<CourseDoc>('courses');
  const allChapters = useOrderedCol<ChapterDoc>('chapters');
  const allSections = useOrderedCol<SectionDoc>('sections');
  const allRes = useOrderedCol<ResourceDoc>('resources');

  const chapters = {
    data: allChapters.data.filter(c => c.courseId === key),
    loading: allChapters.loading,
    error: allChapters.error
  };
  const courseRes = {
    data: allRes.data.filter(r => r.courseId === key),
    loading: allRes.loading,
    error: allRes.error
  };

  const visibleCourses = useMemo(() => courses.data.filter(c => !c.hidden), [courses.data]);
  const visibleChapters = useMemo(() => chapters.data.filter(c => !c.hidden), [chapters.data]);

  // sections grouped by chapterId (sections have no courseId field)
  const sectionsByChapter = useMemo(() => {
    const m = new Map<string, Array<SectionDoc & { id: string }>>();
    for (const s of allSections.data) {
      if (s.hidden) continue;
      const arr = m.get(s.chapterId) ?? [];
      arr.push(s);
      m.set(s.chapterId, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.order - b.order);
    return m;
  }, [allSections.data]);

  const courseResourceIds = useMemo(
    () => courseRes.data.filter(r => !r.hidden).map(r => r.id),
    [courseRes.data]
  );
  const stat = pctOf(courseResourceIds, progress.done);
  const { data: settings } = useSiteSettings();
  const gamified = settings?.gamified !== false;
  const g = gamify(progress.xp);

  const week = useMemo(() => {
    const labels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const out: Array<{ label: string; date: string; active: boolean }> = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      out.push({ label: labels[d.getDay()], date: iso, active: progress.activeDays.includes(iso) });
    }
    return out;
  }, [progress.activeDays]);

  const uDoc = useDoc<{ name?: string }>('users', auth.user?.uid ?? '', auth.isSignedIn);
  const firstName = (uDoc.data?.name || auth.user?.name)?.trim().split(/\s+/)[0];
  const greeting = auth.isSignedIn && firstName ? `Welcome back, ${firstName}.` : 'Explore the chapters';
  const activeCourse = visibleCourses.find(c => c.id === key) ?? visibleCourses[0];

  if (courses.error) {
    return <ErrorBlock message={courses.error} />;
  }

  if (!routeCourse && !courses.loading && visibleCourses.length === 0) {
    return (
      <EmptyState
        glyph="✦"
        title="No courses yet"
        body="Courses will appear here as soon as the teacher publishes them. Check back soon."
      />
    );
  }

  return (
    <div className="page-anim">
      <style>{`.wide-grid{display:grid;grid-template-columns:1fr;gap:16px}
@media(min-width:900px){.wide-grid{grid-template-columns:repeat(auto-fill,minmax(440px,1fr))}}`}</style>
      {/* hero */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 11 }}>
            <span className="tick" aria-hidden />
            <span>
              {activeCourse ? `${activeCourse.code} · ${activeCourse.name}` : 'Student Resources Hub'}
            </span>
          </div>
          <h1 className="display">{greeting}</h1>
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', padding: '16px 20px', display: 'flex', gap: 22, boxShadow: 'var(--shadow-2)' }}>
          <div style={{ background: 'var(--t-green)', borderRadius: 12, padding: '10px 16px' }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 600, color: 'var(--c-green)' }}>{stat.pct}%</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink2)', marginTop: 1 }}>course complete</div>
          </div>
          <div style={{ background: 'var(--t-plum)', borderRadius: 12, padding: '10px 16px' }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 600, color: 'var(--c-plum)' }}>
              {stat.d}/{stat.n}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink2)', marginTop: 1 }}>resources done</div>
          </div>
        </div>
      </div>

      {gamified && auth.isSignedIn && (
        <div className="level-banner">
          <div className="level-main">
            <div className="level-head">
              <span className="level-num">Level {g.level}</span>
              <span className="level-label">{g.label}</span>
            </div>
            <div
              className="level-bar"
              role="progressbar"
              aria-valuenow={g.levelPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Level ${g.level} progress`}
            >
              <div className="level-fill" style={{ width: `${g.levelPct}%` }} />
            </div>
            <div className="level-xp">{g.xpToNext} XP to level {g.nextLevel}</div>
          </div>
          <div className="level-week" role="group" aria-label="Study days, last week">
            {week.map((d, i) => (
              <div key={i} className="day-cell">
                <span
                  className={`day-chip${d.active ? ' on' : ''}${i === 6 ? ' today' : ''}`}
                  title={`${d.date} · ${d.active ? 'studied' : 'no study logged'}`}
                >
                  {d.active ? '✓' : '·'}
                </span>
                <span className="day-label">{d.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <AdSlot variant="leaderboard" />

      {/* chapters */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, margin: '28px 0 16px' }}>
        <div className="eyebrow">
          <span className="tick" aria-hidden />
          <span>Chapters</span>
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', padding: 4, borderRadius: 12 }}>
          {(['tall', 'wide'] as const).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              aria-pressed={view === v}
              style={{
                padding: '7px 14px', border: 'none', borderRadius: 9, fontSize: 13.5, fontWeight: 500,
                background: view === v ? 'var(--surface)' : 'transparent',
                color: view === v ? 'var(--c-green)' : 'var(--ink2)',
                boxShadow: view === v ? '0 2px 6px rgba(23,26,23,.1)' : 'none'
              }}
            >
              {v === 'tall' ? 'Tall' : 'Wide'}
            </button>
          ))}
        </div>
      </div>

      {chapters.error ? (
        <ErrorBlock message={chapters.error} />
      ) : chapters.loading ? (
        <Loading label="Loading chapters…" />
      ) : visibleChapters.length === 0 ? (
        <EmptyState
          glyph="✦"
          title="No chapters yet"
          body="The teacher is setting this course up. Chapter outlines and resources will appear here."
        />
      ) : (
        <div className={view === 'tall' ? 'grid-ch' : 'wide-grid'}>
          {visibleChapters.map((ch, i) => {
            const secs = sectionsByChapter.get(ch.id) ?? [];
            const secSet = new Set(secs.map(s => s.id));
            const resIds = courseRes.data.filter(r => !r.hidden && secSet.has(r.sectionId)).map(r => r.id);
            return <ChapterCard key={ch.id} chapter={ch} index={i} sections={secs} resIds={resIds} done={progress.done} wide={view === 'wide'} />;
          })}
        </div>
      )}

      <AdSlot variant="inline" />
    </div>
  );
}

function ChapterCard({
  chapter,
  index,
  sections,
  resIds,
  done,
  wide
}: {
  chapter: ChapterDoc & { id: string };
  index: number;
  sections: SectionDoc[];
  resIds: string[];
  done: Set<string>;
  wide: boolean;
}) {
  const pal = paletteFor(index);
  const p = pctOf(resIds, done);
  const cta = p.pct === 0 ? 'Start' : p.pct === 100 ? 'Review' : 'Continue';
  const fill = p.pct > 0 ? pal.accent : 'var(--line)';
  const delay = `${(0.05 + index * 0.04).toFixed(2)}s`;

  if (wide) {
    return (
      <Link
        to={`/ch/${chapter.id}`}
        className="card hoverable"
        style={{ padding: 0, overflow: 'hidden', display: 'flex', alignItems: 'stretch', color: 'inherit', textDecoration: 'none', animation: `fadeUp .45s ${delay} ease both` }}
      >
        <span style={{ position: 'relative', flex: 'none', width: 104, background: pal.tint, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, overflow: 'hidden' }}>
          <span style={{ position: 'absolute', left: -14, bottom: -30, fontFamily: 'var(--font-serif)', fontSize: 96, lineHeight: 1, color: pal.accent, opacity: .18 }} aria-hidden>
            {pal.glyph}
          </span>
          <span style={{ position: 'relative', fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '.1em', color: pal.deep }}>CH {chapter.num}</span>
          <span style={{ position: 'relative', fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: pal.deep }}>{p.pct}%</span>
        </span>
        <span style={{ flex: 1, minWidth: 0, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 9 }}>
          <span style={{ display: 'block', fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-.022em', color: 'var(--ink)', textWrap: 'balance' }}>
            {chapter.title}
          </span>
          <span style={{ display: 'block', fontSize: 14, lineHeight: 1.5, color: 'var(--ink2)', textWrap: 'pretty' }}>{chapter.blurb}</span>
          <span style={{ flex: 1 }} />
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ flex: 1, height: 6, borderRadius: 99, background: 'var(--surface2)', overflow: 'hidden', display: 'block' }}>
              <span style={{ display: 'block', height: '100%', borderRadius: 99, background: fill, width: `${p.pct}%`, transition: 'width .5s ease' }} />
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--ink3)' }}>{sections.length} {sections.length === 1 ? 'section' : 'sections'}</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: pal.deep }}>{cta} →</span>
          </span>
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={`/ch/${chapter.id}`}
      className="card hoverable"
      style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', color: 'inherit', textDecoration: 'none', animation: `fadeUp .5s ${delay} ease both` }}
    >
      <div style={{ position: 'relative', height: 96, background: pal.tint, overflow: 'hidden', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px' }}>
        <span style={{ position: 'absolute', right: -10, bottom: -46, fontFamily: 'var(--font-serif)', fontSize: 120, lineHeight: 1, color: pal.accent, opacity: .16 }} aria-hidden>
          {pal.glyph}
        </span>
        <span style={{ position: 'relative', fontFamily: 'var(--font-mono)', fontSize: 12, color: pal.deep, letterSpacing: '.08em', fontWeight: 500 }}>
          CH {chapter.num}
        </span>
        <span style={{ position: 'relative', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500, padding: '5px 10px', borderRadius: 9, background: 'var(--glass)', color: pal.deep }}>
          {p.pct}%
        </span>
      </div>
      <div style={{ padding: '20px 22px 22px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 23, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-.022em', marginBottom: 7, textWrap: 'balance' }}>
            {chapter.title}
          </div>
          <div style={{ fontSize: 14.5, lineHeight: 1.5, color: 'var(--ink2)', textWrap: 'pretty' }}>{chapter.blurb}</div>
        </div>
        <div style={{ flex: 1 }} />
        <div>
          <div style={{ height: 7, borderRadius: 99, background: 'var(--surface2)', overflow: 'hidden', marginBottom: 10 }}>
            <div style={{ height: '100%', borderRadius: 99, background: fill, width: `${p.pct}%`, transition: 'width .5s ease' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--ink3)' }}>
            <span>{sections.length} {sections.length === 1 ? 'section' : 'sections'} · {p.n} {p.n === 1 ? 'resource' : 'resources'}</span>
            <span style={{ color: pal.deep, fontWeight: 500 }}>{cta} →</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
